import { Paper as MuiPaper, Stack, Typography } from '@mui/material'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { AppName } from '../../State/App/AppModel'
import { useTypedSelector } from '../../State/RootReducer'

interface StyleProps {
  app?: AppName
  hasIcon?: boolean
}

const useStyles = makeStyles<StyleProps>()(theme => ({
  paper: {
    display: 'flex',
    padding: theme.spacing(4),
    paddingTop: 0,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  searchBar: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  contentWrapper: {
    position: 'relative',
    flex: 1,
    height: '100%',
    zIndex: 1,
    /* TODO: this should likely be removed since it can lead to unexpected padding behavior when reaching breakpoint md */
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      marginRight: 0,
    },
  },
  AppBarWhite: {
    backgroundColor: theme.palette.primary.dark,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 0,
    paddingTop: theme.spacing(2),
    marginBottom: theme.shape.header.marginBottom,
    position: 'relative',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  headline: {
    color: theme.palette.text.paperTitle,
  },
  noMargin: {
    margin: 0,
  },
}))

export interface PaperProps {
  title?: React.ReactNode
  subTitle?: React.ReactNode
  fullHeight?: boolean
  headerChildren?: React.ReactNode
  noMargin?: boolean
  className?: string
  contentClassName?: string
  wrapperClassName?: string
  headerClassName?: string
  elevation?: number
  onClick?: () => void
  id?: string
  innerRef?: React.Ref<HTMLDivElement>
  paperRef?: React.Ref<HTMLDivElement>
  appBarClass?: string
  headlineClass?: string
  dataQa?: string
  children?: React.ReactNode
}

const Paper: React.FC<PaperProps> = ({
  title,
  subTitle,
  fullHeight,
  headerChildren,
  noMargin,
  children,
  className,
  contentClassName,
  wrapperClassName,
  headerClassName,
  elevation,
  onClick,
  id,
  innerRef,
  paperRef,
  appBarClass,
  headlineClass,
  dataQa,
}) => {
  const app = useTypedSelector(state => state.app.app)
  const { classes, cx } = useStyles({ app, hasIcon: false })
  const paperFullHeight = fullHeight ? { height: '100%' } : {}

  return (
    <MuiPaper
      ref={paperRef}
      className={cx(classes.paper, className)}
      style={{ ...paperFullHeight }}
      elevation={elevation}
      onClick={onClick}
      id={id}
      data-qa={dataQa}
    >
      <div className={cx(classes.wrapper, wrapperClassName)}>
        {(title || headerChildren) && (
          <div className={(classes.searchBar, classes.AppBarWhite, appBarClass)} data-qa={'paper-header'}>
            <div className={cx(classes.header, headerClassName)}>
              <div className={classes.headerContent}>
                <Stack direction="column">
                  <Stack direction="row">
                    <Typography variant={'h3'} className={cx(classes.headline, headlineClass)}>
                      {title}
                    </Typography>
                  </Stack>
                  {subTitle && <Typography variant={'caption'}>{subTitle}</Typography>}
                </Stack>
                {headerChildren && <div>{headerChildren}</div>}
              </div>
            </div>
          </div>
        )}
        {children && (
          <div className={cx(classes.contentWrapper, contentClassName, [{ [classes.noMargin]: noMargin }])} ref={innerRef}>
            {children}
          </div>
        )}
      </div>
    </MuiPaper>
  )
}

export default Paper
